// TEMPORARY: standalone Google Pay test page used only to capture the
// screenshot required by the Google Pay Business Console production-access
// review. Loads Google Pay's JS API in TEST mode (no Tyro involvement, no real
// money). Delete this file and its route once GP production approval lands.
import React, { useEffect, useRef, useState } from 'react';

const GP_SCRIPT_SRC = 'https://pay.google.com/gp/p/js/pay.js';

function loadGoogleScript() {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.payments && window.google.payments.api) {
      resolve();
      return;
    }
    const existing = document.querySelector(`script[src="${GP_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', resolve, { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }
    const s = document.createElement('script');
    s.src = GP_SCRIPT_SRC;
    s.async = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error('Failed to load pay.js'));
    document.head.appendChild(s);
  });
}

export default function GpTestPage() {
  const [status, setStatus] = useState('Loading Google Pay…');
  const [error, setError] = useState(null);
  const buttonContainerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await loadGoogleScript();
        if (cancelled) return;

        const paymentsClient = new window.google.payments.api.PaymentsClient({
          environment: 'TEST',
        });

        const baseRequest = { apiVersion: 2, apiVersionMinor: 0 };

        const allowedPaymentMethods = [
          {
            type: 'CARD',
            parameters: {
              allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
              allowedCardNetworks: ['VISA', 'MASTERCARD', 'AMEX'],
            },
            tokenizationSpecification: {
              type: 'PAYMENT_GATEWAY',
              parameters: {
                gateway: 'example',
                gatewayMerchantId: 'exampleGatewayMerchantId',
              },
            },
          },
        ];

        const readyToPay = await paymentsClient.isReadyToPay({
          ...baseRequest,
          allowedPaymentMethods,
        });
        if (cancelled) return;

        if (!readyToPay.result) {
          setStatus(
            'Google Pay is not available on this device. Make sure you are on an Android device with Google Pay set up and at least one saved card.'
          );
          return;
        }

        const button = paymentsClient.createButton({
          buttonColor: 'default',
          buttonType: 'pay',
          buttonSizeMode: 'fill',
          onClick: async () => {
            try {
              setError(null);
              await paymentsClient.loadPaymentData({
                ...baseRequest,
                allowedPaymentMethods,
                transactionInfo: {
                  totalPriceStatus: 'FINAL',
                  totalPrice: '12.00',
                  currencyCode: 'AUD',
                  countryCode: 'AU',
                },
                merchantInfo: { merchantName: 'St Barbara Kitchen' },
              });
              setStatus('Sheet returned. You can back out — no payment was processed.');
            } catch (err) {
              if (err && err.statusCode === 'CANCELED') {
                setStatus('Sheet cancelled. Tap the Google Pay button again if you need another go.');
              } else {
                setError(`Google Pay error: ${err?.statusMessage || err?.message || 'Unknown'}`);
              }
            }
          },
        });

        if (!cancelled && buttonContainerRef.current) {
          buttonContainerRef.current.innerHTML = '';
          buttonContainerRef.current.appendChild(button);
          setStatus(
            'Ready. Tap the Google Pay button below, then photograph the sheet that appears with another camera (Android blocks screenshots of the GP sheet).'
          );
        }
      } catch (err) {
        if (!cancelled) setError(`Failed to load Google Pay: ${err.message}`);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  return (
    <main className="container" style={{ padding: '1.5rem', maxWidth: 480, margin: '0 auto' }}>
      <h2 style={{ marginTop: 0 }}>Google Pay Screenshot Test</h2>
      <p style={{ fontSize: 14, lineHeight: 1.5, color: '#555' }}>
        This page is for capturing the screenshot required by the Google Pay Business
        Console production review. It uses Google Pay's <strong>test</strong> environment —
        no Tyro involvement, no real card charge.
      </p>

      <ol style={{ fontSize: 14, lineHeight: 1.6, color: '#555' }}>
        <li>Tap the Google Pay button below.</li>
        <li>The Google Pay sheet will open showing your saved cards.</li>
        <li>Photograph the screen with a <em>different</em> camera (Android blocks screenshots).</li>
        <li>Back out — nothing is charged.</li>
        <li>Upload the photo to the Business Console.</li>
      </ol>

      <div
        ref={buttonContainerRef}
        style={{ margin: '24px 0', minHeight: 48, display: 'flex', justifyContent: 'center' }}
      />

      <p style={{ fontSize: 13, color: '#666' }}>{status}</p>
      {error && (
        <p style={{ color: '#c00', fontSize: 13, padding: 8, background: '#fee', borderRadius: 4 }}>
          {error}
        </p>
      )}

      <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #eee' }} />
      <p style={{ fontSize: 11, color: '#999' }}>
        Temporary route. Will be removed once Google Pay production access is approved.
      </p>
    </main>
  );
}
